import { createCaseApi, type AuditOverride } from '../api/cases.js';
import { createEmptyKyc, createEmptyOnboarding } from '../domain/factories';
import type { Case } from '../domain/initial-cases';
import { newIdempotencyKey } from '../lib/idempotency.js';

export interface CreateCaseInput {
  status: string;
  registrant?: string;
  analystId?: string | null;
  action?: AuditOverride;
}

export async function createCase(input: CreateCaseInput): Promise<Case> {
  const idempotencyKey = newIdempotencyKey();
  return createCaseApi(
    {
      // The API requires registrant to be a non-empty string. Use a
      // placeholder for fresh cases; the user fills the real legal name
      // in via the onboarding form (which then PATCHes registrant).
      registrant: input.registrant?.trim() || 'New Application',
      status: input.status,
      // #693: no oaName/oaEmail. 382 is the Onboarding Agent on every case, so
      // the two fields described 382 to itself; the notice recipient comes from
      // the registrant's own submission now (`notificationContact` in
      // state/actions/lifecycle.ts). Seeding '' here would have made every new
      // case un-notifiable-looking the moment the Overview inputs went away.
      // #692: no oaIsCompany. 382 is the Onboarding Agent on every
      // application, so the flag was a constant, and it chose the vetting SLA
      // term. The server no longer reads it and no longer accepts it.
      analystId: input.analystId ?? null,
      // #286: no slaDeadline. Creating into IN_REVIEW is the server's trigger to
      // compute the vetting SLA (15 business days) from its own clock — this
      // used to be computed here off the analyst's workstation clock and stored
      // verbatim. The old `withSlaDeadline` input flag went with it: the client
      // no longer has any say, and a flag that reads like it toggles a
      // compliance deadline while doing nothing is worse than no flag.
      kyc: createEmptyKyc(),
      onboarding: createEmptyOnboarding(),
      action: input.action ?? { code: 'CASE_CREATED' },
    } as Partial<Case> & { action?: AuditOverride },
    idempotencyKey,
  );
}
